import './App.css';

import React, { JSX, useContext } from 'react';
import { BrowserRouter, Navigate, Routes as ReactRoutes, Route, useLocation } from 'react-router';
import { EmailConfirmationPage } from '../pages/EmailConfirmationPage/EmailConfirmationPage';
import { ForgotPasswordPage } from '../pages/ForgotPasswordPage/ForgotPasswordPage';
import { LoginPage } from '../pages/LoginPage/LoginPage';
import { NewPasswordPage } from '../pages/NewPasswordPage/NewPasswordPage';
import { NotFoundPage } from '../pages/NotFoundPage/NotFoundPage';
import { OrderPrintPage } from '../pages/OrderPrintPage/OrderPrintPage';
import { SignUpPage } from '../pages/SignUpPage/SignUpPage';
import { WaitingForApprovalPage } from '../pages/WaitingForApprovalPage/WaitingForApprovalPage';
import { WelcomePage } from '../pages/WelcomePage/WelcomePage';
import { AdminCustomersPage } from '../pages/administration/AdminCustomersPage/AdminCustomersPage';
import { AdminOrderDetailsPage } from '../pages/administration/AdminOrderDetailsPage/AdminOrderDetailsPage';
import { AdminOrdersPage } from '../pages/administration/AdminOrdersPage/AdminOrdersPage';
import { AdminUsersPage } from '../pages/administration/AdminUsersPage/AdminUsersPage';
import { CalculationPage } from '../pages/checkout/CalculationPage/CalculationPage';
import { Checkout } from '../pages/checkout/Checkout';
import { OrderPage } from '../pages/checkout/OrderPage/OrderPage';
import { ParametersPage } from '../pages/checkout/ParametersPage/ParametersPage';
import { SuccessPage } from '../pages/checkout/SuccessPage/SuccessPage';
import { UploadPage } from '../pages/checkout/UploadPage/UploadPage';
import { CustomerOrderDetailsPage } from '../pages/customer/CustomerOrderDetailsPage/CustomerOrderDetailsPage';
import { CustomerOrdersPage } from '../pages/customer/CustomerOrdersPage/CustomerOrdersPage';
import { UserSettingsPage } from '../pages/user/UserSettingsPage/UserSettingsPage';
import { AuthContext, AuthContextProps } from '../services/AuthContext';
import { Routes } from './Routes';

export function AppRouter(): JSX.Element | null {
  const { user, isActive, isAdmin, isAuthenticationDone } = useContext<AuthContextProps>(AuthContext);

  const isEmailVerified = () => {
    return user?.emailVerified;
  };

  const isWaitingForApproval = () => {
    return user?.emailVerified && !isActive;
  };

  function PublicOnly({ children }: { readonly children: React.ReactElement }) {
    const location = useLocation();

    if (user) {
      return <Navigate to={Routes.START_PAGE} replace state={{ from: location }} />;
    }
    return children;
  }

  function Protected({
    adminOnly = false,
    children,
  }: {
    readonly adminOnly?: boolean;
    readonly children: React.ReactElement;
  }) {
    const location = useLocation();

    const isLoggedIn = !!user;
    let redirectTo: string | null = null;

    if (!isLoggedIn) {
      redirectTo = Routes.LOGIN;
    } else if (!isEmailVerified()) {
      redirectTo = Routes.EMAIL_CONFIRMATION;
    } else if (isWaitingForApproval()) {
      redirectTo = Routes.WAITING_FOR_APPROVAL;
    } else if (adminOnly && !isAdmin) {
      redirectTo = Routes.START_PAGE;
    }

    if (redirectTo) {
      return <Navigate to={redirectTo} replace state={{ from: location }} />;
    }
    return children;
  }

  function ProtectedConditional({
    condition,
    fallbackPath,
    children,
  }: {
    readonly condition: boolean | undefined;
    readonly fallbackPath: string;
    readonly children: React.ReactElement;
  }) {
    const location = useLocation();
    const isLoggedIn = !!user;

    if (!isLoggedIn || !condition) {
      return <Navigate to={!isLoggedIn ? Routes.LOGIN : fallbackPath} replace state={{ from: location }} />;
    }
    return children;
  }

  function WaitingForApproval({
    isWaitingForApproval,
  }: {
    readonly isWaitingForApproval: boolean | undefined;
  }): JSX.Element {
    const location = useLocation();
    if (!user || !isWaitingForApproval) {
      return <Navigate to={!user ? Routes.LOGIN : Routes.START_PAGE} replace state={{ from: location }} />;
    }
    return <WaitingForApprovalPage />;
  }

  if (!isAuthenticationDone) {
    return null;
  }

  return (
    <main className="app_router">
      <BrowserRouter>
        <ReactRoutes>
          <Route
            path={Routes.WAITING_FOR_APPROVAL}
            element={<WaitingForApproval isWaitingForApproval={isWaitingForApproval()} />}
          />
          <Route
            path={Routes.adminUsers(':status')}
            element={
              <Protected adminOnly>
                <AdminUsersPage />
              </Protected>
            }
          />
          <Route
            path={Routes.ADMIN_CUSTOMERS}
            element={
              <Protected adminOnly>
                <AdminCustomersPage />
              </Protected>
            }
          />
          <Route
            path={Routes.adminOrders(':status')}
            element={
              <Protected adminOnly>
                <AdminOrdersPage />
              </Protected>
            }
          />
          <Route
            path={Routes.WELCOME_PAGE}
            element={
              <ProtectedConditional condition={isWaitingForApproval()} fallbackPath={Routes.WAITING_FOR_APPROVAL}>
                <WelcomePage />
              </ProtectedConditional>
            }
          />
          <Route
            path={Routes.CHECKOUT}
            element={
              <Protected>
                <Navigate to={Routes.CHECKOUT_UPLOAD} />
              </Protected>
            }
          />
          <Route
            path={Routes.CHECKOUT_UPLOAD}
            element={
              <Protected>
                <Checkout component={UploadPage} />
              </Protected>
            }
          />
          <Route
            path={Routes.checkoutUpload(':id')}
            element={
              <Protected>
                <Checkout component={UploadPage} />
              </Protected>
            }
          />
          <Route
            path={Routes.checkoutParameters(':id')}
            element={
              <Protected>
                <Checkout component={ParametersPage} />
              </Protected>
            }
          />
          <Route
            path={Routes.checkoutCalculation(':id')}
            element={
              <Protected>
                <Checkout component={CalculationPage} />
              </Protected>
            }
          />
          <Route
            path={Routes.checkoutOrder(':id')}
            element={
              <Protected>
                <Checkout component={OrderPage} />
              </Protected>
            }
          />
          <Route
            path={Routes.checkoutSuccess(':id')}
            element={
              <Protected>
                <Checkout component={SuccessPage} />
              </Protected>
            }
          />
          <Route
            path={Routes.orderPrint(':id')}
            element={
              <Protected>
                <OrderPrintPage />
              </Protected>
            }
          />
          <Route
            path={Routes.CUSTOMER_ORDERS}
            element={
              <Protected>
                <CustomerOrdersPage />
              </Protected>
            }
          />
          <Route
            path={Routes.customerOrdersDetails(':id')}
            element={
              <Protected>
                <CustomerOrderDetailsPage />
              </Protected>
            }
          />
          <Route
            path={'/'}
            element={
              <Protected>
                <Navigate to={Routes.CHECKOUT_UPLOAD} />
              </Protected>
            }
          />
          <Route
            path={Routes.userSettings()}
            element={
              <Protected>
                <Navigate to={Routes.userSettings('shippingAddress')} />
              </Protected>
            }
          />
          <Route
            path={Routes.userSettings(':setting')}
            element={
              <Protected>
                <UserSettingsPage />
              </Protected>
            }
          />
          <Route
            path={Routes.adminOrderDetails(':id')}
            element={
              <Protected>
                <AdminOrderDetailsPage />
              </Protected>
            }
          />
          <Route
            path={Routes.NEW_PASSWORD}
            element={
              <PublicOnly>
                <NewPasswordPage />
              </PublicOnly>
            }
          />
          <Route
            path={Routes.FORGOT_PASSWORD}
            element={
              <PublicOnly>
                <ForgotPasswordPage />
              </PublicOnly>
            }
          />
          <Route
            path={Routes.LOGIN}
            element={
              <PublicOnly>
                <LoginPage />
              </PublicOnly>
            }
          />
          <Route
            path={Routes.SIGN_UP}
            element={
              <PublicOnly>
                <SignUpPage />
              </PublicOnly>
            }
          />
          <Route
            path={Routes.EMAIL_CONFIRMATION}
            element={
              <PublicOnly>
                <EmailConfirmationPage />
              </PublicOnly>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </ReactRoutes>
      </BrowserRouter>
    </main>
  );
}
